import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MapPin } from 'lucide-react';

const NavLink: React.FC<{ to: string; children: React.ReactNode; onClick?: () => void; isScrolled: boolean }> = ({ to, children, onClick }) => {
  const location = useLocation();
  const isActive = location.pathname === to || (to !== '/' && location.pathname.startsWith(to));
  const textColor = "text-white hover:text-white/80";

  return (
    <Link
      to={to}
      onClick={onClick}
      className={`relative px-4 flex items-center text-sm font-bold uppercase tracking-wider transition-colors duration-200 group h-full ${
        isActive ? 'text-white' : textColor
      }`}
    >
      {children}
      <span className={`absolute bottom-0 left-0 h-1 bg-brand-red transition-all duration-200 ease-linear ${isActive ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
    </Link>
  );
};

export const Header: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const closeMenu = () => setIsOpen(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className="fixed w-full top-0 z-50 flex flex-col shadow-lg">
      <div className={`w-full bg-gradient-to-r from-brand-red-dark via-brand-red to-brand-red-dark text-white text-xs py-2 px-6 flex justify-between items-center z-50 transition-all duration-300 ${isScrolled ? 'h-0 py-0 opacity-0 overflow-hidden' : 'h-10 opacity-100'}`}>
         <div className="flex gap-6 font-mono tracking-tight">
           <span className="flex items-center gap-2"><MapPin size={12} /> 875, RUE DUFFERIN, GRANBY</span>
           <span className="flex items-center gap-2"><Phone size={12} /> 450 372-2565</span>
         </div>
         <div className="flex gap-4 font-bold uppercase tracking-wider hidden md:flex">
            <span>DEPUIS 1983</span>
            <span className="text-white/50">|</span>
            <span>RBQ: 8350-1361-26</span>
         </div>
      </div>

      <nav className={`w-full bg-brand-blue border-b border-white/10 transition-all duration-500 ease-in-out`}>
        <div className={`container mx-auto px-6 flex justify-between items-center transition-all duration-500 ${isScrolled ? 'h-12' : 'h-20'}`}>
          <Link to="/" className={`group flex items-center gap-3 z-50 transition-all duration-500 overflow-hidden w-auto opacity-100`} onClick={closeMenu}>
             <img 
               src="https://betonmobiledescantons.com/images/logo-beton-mobile-des-cantons-fr.png" 
               alt="Béton Mobile des Cantons inc." 
               className={`w-auto object-contain drop-shadow-md hover:scale-105 transition-all duration-300 ${isScrolled ? 'h-8' : 'h-14 md:h-16'}`}
             />
          </Link>

          <div className="hidden lg:flex items-stretch h-full ml-auto">
            <NavLink to="/" onClick={closeMenu} isScrolled={isScrolled}>Accueil</NavLink>
            <NavLink to="/beton" onClick={closeMenu} isScrolled={isScrolled}>Béton</NavLink>
            <NavLink to="/plancher-radiant" onClick={closeMenu} isScrolled={isScrolled}>Plancher Radiant</NavLink>
            <NavLink to="/coffrage-et-fondation" onClick={closeMenu} isScrolled={isScrolled}>Coffrage</NavLink>
            <NavLink to="/services" onClick={closeMenu} isScrolled={isScrolled}>Services</NavLink>
            <NavLink to="/realisations" onClick={closeMenu} isScrolled={isScrolled}>Réalisations</NavLink>
            <NavLink to="/contact" onClick={closeMenu} isScrolled={isScrolled}>Contact</NavLink>
          </div>

          <div className="hidden lg:flex items-center ml-6">
             <Link to="/contact" className={`px-4 py-2 font-bold uppercase text-xs tracking-wider border-2 transition-all duration-200 bg-brand-red text-white border-brand-red hover:bg-white hover:text-brand-red`}>
               Soumission
             </Link>
          </div>

          <button className={`lg:hidden p-2 z-50 text-white`} onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <X size={28} className="text-white" /> : <Menu size={28} />}
          </button>
        </div>

        <div className={`fixed inset-0 bg-brand-blue z-40 transition-transform duration-300 transform ${isOpen ? 'translate-x-0' : 'translate-x-full'} lg:hidden flex flex-col pt-32 px-6 space-y-6 shadow-2xl overflow-y-auto`}>
           {[
             { path: "/", label: "Accueil" },
             { path: "/beton", label: "Béton" },
             { path: "/plancher-radiant", label: "Plancher Radiant" },
             { path: "/coffrage-et-fondation", label: "Coffrage" },
             { path: "/services", label: "Services" },
             { path: "/realisations", label: "Réalisations" },
             { path: "/contact", label: "Contact" },
             { path: "/plan-du-site", label: "Plan du site" }
           ].map((item) => (
             <Link 
               key={item.path} 
               to={item.path} 
               onClick={closeMenu}
               className="text-2xl font-display font-bold text-white border-b border-white/10 pb-4 uppercase hover:text-brand-red transition-colors"
             >
               {item.label}
             </Link>
           ))}
           <div className="pt-4">
             <Link to="/contact" onClick={closeMenu} className="block w-full text-center bg-brand-red text-white py-4 font-bold uppercase tracking-widest border border-brand-red hover:bg-white hover:text-brand-red">
               Demander une soumission
             </Link>
           </div>
        </div>
      </nav>
    </header>
  );
};